/**
 * useVersionCheck — polls /api/version every 5 minutes; when the live
 * backend SHA drifts from the bundle's REACT_APP_GIT_SHA (baked at build
 * time by scripts/write-git-sha.js), fires a single sticky Sonner toast
 * with a "Refresh" action.
 *
 * Why: long-lived tabs (24h+) routinely hit cookie/CSRF mismatches against
 * a freshly-deployed backend. The /api/version endpoint added in iter-48
 * gives us a cheap way to detect this client-side without a websocket
 * push. The toast is dismissable but won't auto-disappear — users need to
 * either refresh or actively dismiss it.
 *
 * No-op in development (REACT_APP_GIT_SHA falls back to 'unknown' there)
 * and when the bundle SHA is itself 'unknown' (avoids false positives).
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { reportInfo } from '../lib/sentry';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Read BUNDLE_SHA lazily so unit tests can patch process.env per case
// without juggling `jest.resetModules()`. In production this is evaluated
// exactly once per hook instance (mount).
const getBundleSha = () => process.env.REACT_APP_GIT_SHA || 'unknown';

const fetchVersion = async () => {
  const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/version`, {
    timeout: 5000,
  });
  return res.data?.git_sha || null;
};

export const useVersionCheck = (enabled = true) => {
  // Track whether we've already prompted in this session so we don't spam.
  const promptedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const bundleSha = getBundleSha();
    // No comparison possible if the bundle itself doesn't know its SHA
    // (local dev, CI build without git context, etc.).
    if (bundleSha === 'unknown') return undefined;

    let cancelled = false;

    const check = async () => {
      try {
        const liveSha = await fetchVersion();
        if (cancelled || !liveSha) return;
        if (liveSha !== bundleSha && !promptedRef.current) {
          promptedRef.current = true;
          toast.message('A new version of TI Networking is available', {
            description: `You're on ${bundleSha}, the server is on ${liveSha}. Refresh to avoid login/CSRF errors.`,
            duration: Infinity, // sticky — user must dismiss or refresh
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload(),
            },
            id: 'version-mismatch', // dedupe in case of effect re-runs
          });
          // Telemetry: how often does this actually fire in the wild? Helps
          // tune the 5-min poll interval. Sentry sees this as an info-level
          // event, NOT an error.
          reportInfo('version-drift detected', {
            frontend_sha: bundleSha,
            backend_sha: liveSha,
            route: typeof window !== 'undefined' ? window.location.pathname : null,
          });
        }
      } catch (err) {
        console.debug('[useVersionCheck] poll failed (retrying next interval):', err?.message);
      }
    };

    // First check after 30s (gives the app time to settle / auth to load)
    // then every 5min thereafter.
    const initial = setTimeout(check, 30 * 1000);
    timerRef.current = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(initial);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled]);
};

export default useVersionCheck;
